import { useEffect, useState, useCallback, useRef } from "react";
import {
    FaDatabase,
    FaRobot,
    FaServer,
    FaCheckCircle,
    FaTimesCircle,
    FaSyncAlt,
    FaClock,
    FaHistory
} from "react-icons/fa";

import "./Dashboard.css";

// API_URL is the API base only, e.g. http://172.20.10.2:8000/api
// The actual health route is appended below as HEALTH_ENDPOINT.
const API_URL = process.env.REACT_APP_API_URL;
const API_HEALTH_ENDPOINT = process.env.REACT_APP_API_URL_HEALTH;

// NOTE: must be prefixed with REACT_APP_ or Create React App will not
// expose it to the browser bundle (process.env.SOAP_API_URL would
// always be undefined without the prefix).
const SOAP_API_URL = process.env.REACT_APP_SOAP_API_URL;

// Per the Swagger doc: GET /api/admin/health
const HEALTH_ENDPOINT = `${API_HEALTH_ENDPOINT}/admin/health`;

// Per the Swagger doc: GET /api/portal/last and GET /api/portal/list
const LAST_SYNC_ENDPOINT = `${API_URL}/portal/last`;
const LOG_LIST_ENDPOINT = `${API_URL}/portal/list`;

// Per the Swagger doc: GET /api/portal/robots/fleet/summary
const FLEET_SUMMARY_ENDPOINT = `${API_URL}/portal/robots/fleet/summary`;

const showSoapCard = false;

const SOAP_REQUEST_BODY = `<?xml version="1.0" encoding="UTF-8"?>
<soap:Envelope
    xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"
    xmlns:wsse="http://schemas.xmlsoap.org/ws/2002/12/secext">
    <soap:Header>
        <wsse:Security>
            <wsse:UsernameToken>
                <wsse:Username>tcs_user</wsse:Username>
                <wsse:Password>tcs_password</wsse:Password>
            </wsse:UsernameToken>
        </wsse:Security>
    </soap:Header>
    <soap:Body>
        <HealthCheck/>
    </soap:Body>
</soap:Envelope>`;

const AUTO_REFRESH_MS = 60000; // refresh every 60 seconds

function StatusBadge({ ok }) {

    return (

        <span className={`status-badge ${ok ? "status-ok" : "status-down"}`}>

            {ok ? <FaCheckCircle /> : <FaTimesCircle />}

            {ok ? "Online" : "Down"}

        </span>

    );

}

// Shows two related sub-services (Database + LLM) in a single card,
// each with its own status badge/message, since they're two distinct
// health checks that happen to come from the same /admin/health call.
function CombinedServiceCard({ icon, title, loading, subItems, checkedAt, errorMessage }) {

    const allOk = !loading && subItems.every(item => item.ok);

    return (

        <div className={`service-card ${loading ? "is-loading" : allOk ? "card-ok" : "card-down"}`}>

            <div className="service-card-top">

                <div className="service-icon">
                    {icon}
                </div>

                {
                    loading
                        ? <span className="status-badge status-checking">
                            <FaSyncAlt className="spin" /> Checking
                        </span>
                        : <StatusBadge ok={allOk} />
                }

            </div>

            <h3>{title}</h3>

            <div className="service-subitem-list">

                {subItems.map((item) => (

                    <div className="service-subitem" key={item.label}>

                        <span className="service-subitem-label">{item.label}</span>

                        {
                            loading
                                ? <span className="status-badge status-checking">
                                    <FaSyncAlt className="spin" /> Checking
                                </span>
                                : <StatusBadge ok={item.ok} />
                        }

                        <span className="service-subitem-message">
                            {loading ? "" : (errorMessage || item.message || "No message")}
                        </span>

                    </div>

                ))}

            </div>

            {

                checkedAt &&

                <div className="service-extra">
                    <span className="extra-line">
                        Checked at: {formatDateTime(checkedAt)}
                    </span>
                </div>

            }

        </div>

    );

}

// Robot Fleet at-a-glance - total count plus a breakdown of
// online/offline/idle/low-battery/charging, from
// GET /portal/robots/fleet/summary.
function FleetSummaryCard({ loading, summary, errorMessage }) {

    const ok = !loading && !errorMessage && summary && summary.offline === 0;

    return (

        <div className={`service-card ${loading ? "is-loading" : ok ? "card-ok" : "card-down"}`}>

            <div className="service-card-top">

                <div className="service-icon">
                    <FaRobot />
                </div>

                {
                    loading
                        ? <span className="status-badge status-checking">
                            <FaSyncAlt className="spin" /> Checking
                        </span>
                        : <StatusBadge ok={ok} />
                }

            </div>

            <h3>Robot Fleet</h3>

            <p className="service-message">
                {

                    loading

                        ? "Checking fleet status..."

                        : errorMessage

                            ? errorMessage

                            : summary

                                ? `${summary.total_robots} robot${summary.total_robots === 1 ? "" : "s"} registered`

                                : "No data"

                }
            </p>

            {

                !loading && !errorMessage && summary &&

                <div className="fleet-count-grid">

                    <div className="fleet-count-item fleet-count-online">
                        <span className="fleet-count-value">{summary.online}</span>
                        <span className="fleet-count-label">Online</span>
                    </div>

                    <div className="fleet-count-item fleet-count-offline">
                        <span className="fleet-count-value">{summary.offline}</span>
                        <span className="fleet-count-label">Offline</span>
                    </div>

                    <div className="fleet-count-item">
                        <span className="fleet-count-value">{summary.idle}</span>
                        <span className="fleet-count-label">Idle</span>
                    </div>

                    <div className="fleet-count-item">
                        <span className="fleet-count-value">{summary.charging}</span>
                        <span className="fleet-count-label">Charging</span>
                    </div>

                    <div className="fleet-count-item fleet-count-low-battery">
                        <span className="fleet-count-value">{summary.low_battery}</span>
                        <span className="fleet-count-label">Low Battery</span>
                    </div>

                </div>

            }

        </div>

    );

}



function ServiceCard({ icon, title, ok, message, extra, loading }) {

    return (

        <div className={`service-card ${loading ? "is-loading" : ok ? "card-ok" : "card-down"}`}>

            <div className="service-card-top">

                <div className="service-icon">
                    {icon}
                </div>

                {

                    loading

                        ? <span className="status-badge status-checking">
                            <FaSyncAlt className="spin" /> Checking
                        </span>

                        : <StatusBadge ok={ok} />

                }

            </div>

            <h3>{title}</h3>

            <p className="service-message">
                {loading ? "Checking service status..." : message || "No message"}
            </p>

            {

                extra &&

                <div className="service-extra">
                    {extra}
                </div>

            }

        </div>

    );

}

function formatDateTime(value) {

    if (!value) {
        return "-";
    }

    return new Date(value).toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    });

}

// Returns today's date as YYYY-MM-DD (local time), matching the
// format <input type="date"> uses and what FastAPI's Optional[date]
// query params expect.
function getTodayDateString() {

    const now = new Date();

    const year = now.getFullYear();

    const month = String(now.getMonth() + 1).padStart(2, "0");

    const day = String(now.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;

}

function Dashboard() {

    const [health, setHealth] = useState(null);       // REST /admin/health payload
    const [healthLoading, setHealthLoading] = useState(true);
    const [healthError, setHealthError] = useState(null);

    const [soap, setSoap] = useState(null);            // parsed SOAP payload
    const [soapLoading, setSoapLoading] = useState(true);
    const [soapError, setSoapError] = useState(null);

    const [lastSync, setLastSync] = useState(null);    // GET /portal/last payload
    const [lastSyncLoading, setLastSyncLoading] = useState(true);
    const [lastSyncError, setLastSyncError] = useState(null);

    const [logs, setLogs] = useState([]);              // GET /portal/list payload
    const [logsLoading, setLogsLoading] = useState(true);
    const [logsError, setLogsError] = useState(null);

    const [fleetSummary, setFleetSummary] = useState(null);   // GET /portal/robots/fleet/summary payload
    const [fleetSummaryLoading, setFleetSummaryLoading] = useState(true);
    const [fleetSummaryError, setFleetSummaryError] = useState(null);

    const [statusFilter, setStatusFilter] = useState("all"); // all | success | failed
    const [fromDate, setFromDate] = useState(getTodayDateString());
    const [toDate, setToDate] = useState(getTodayDateString());

    // How many rows to fetch - always starts at 100 on page load,
    // matching the backend's own default. Changing the dropdown
    // re-fetches with the new value; it never affects the initial load.
    const [logLimit, setLogLimit] = useState(100);

    const [lastChecked, setLastChecked] = useState(null);

    const intervalRef = useRef(null);

    const checkRestHealth = useCallback(async () => {

        setHealthLoading(true);
        setHealthError(null);

        try {

            const res = await fetch(HEALTH_ENDPOINT, {
                headers: {
                    accept: "application/json"
                }
            });

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            setHealth(data);

        } catch (err) {

            console.error("Health check failed:", err);

            setHealthError(err.message || "Unable to reach health API");

            setHealth(null);

        } finally {

            setHealthLoading(false);

        }

    }, []);

    const checkSoapHealth = useCallback(async () => {

        setSoapLoading(true);
        setSoapError(null);

        try {

            const res = await fetch(SOAP_API_URL, {

                method: "POST",

                headers: {
                    "Content-Type": "text/xml; charset=utf-8"
                },

                body: SOAP_REQUEST_BODY

            });

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const xmlText = await res.text();

            const parser = new DOMParser();

            const xmlDoc = parser.parseFromString(xmlText, "text/xml");

            const parserErr = xmlDoc.querySelector("parsererror");

            if (parserErr) {
                throw new Error("Invalid XML response from SOAP service");
            }

            const getTag = (tag) => {
                const node = xmlDoc.getElementsByTagName(tag)[0];
                return node ? node.textContent : null;
            };

            const status = getTag("Status");

            setSoap({
                ok: status === "true" || status === "1",
                message: getTag("Message"),
                databaseStatus: getTag("DatabaseStatus"),
                dateTime: getTag("DateTime")
            });

        } catch (err) {

            console.error("SOAP health check failed:", err);

            setSoapError(err.message || "Unable to reach SOAP API");

            setSoap(null);

        } finally {

            setSoapLoading(false);

        }

    }, []);

    // =====================================================
    // Last Sync (GET /portal/last)
    // =====================================================
    const checkLastSync = useCallback(async () => {

        setLastSyncLoading(true);
        setLastSyncError(null);

        try {

            const res = await fetch(LAST_SYNC_ENDPOINT, {
                headers: {
                    accept: "application/json"
                }
            });

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            setLastSync(data);

        } catch (err) {

            console.error("Last sync check failed:", err);

            setLastSyncError(err.message || "Unable to reach last sync API");

            setLastSync(null);

        } finally {

            setLastSyncLoading(false);

        }

    }, []);

    // =====================================================
    // Transaction Log List (GET /portal/list)
    // =====================================================
    const fetchLogs = useCallback(async (from, to, limit) => {

        setLogsLoading(true);
        setLogsError(null);

        try {

            const params = new URLSearchParams();

            if (from) {
                params.append("from_date", from);
            }

            if (to) {
                params.append("to_date", to);
            }

            params.append("limit", limit || 100);

            const qs = params.toString();

            const url = qs ? `${LOG_LIST_ENDPOINT}?${qs}` : LOG_LIST_ENDPOINT;

            const res = await fetch(url, {
                headers: {
                    accept: "application/json"
                }
            });

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            setLogs(Array.isArray(data) ? data : []);

        } catch (err) {

            console.error("Log list fetch failed:", err);

            setLogsError(err.message || "Unable to reach log list API");

            setLogs([]);

        } finally {

            setLogsLoading(false);

        }

    }, []);

    // =====================================================
    // Robot Fleet Summary (GET /portal/robots/fleet/summary)
    // =====================================================
    const checkFleetSummary = useCallback(async () => {

        setFleetSummaryLoading(true);
        setFleetSummaryError(null);

        try {

            const res = await fetch(FLEET_SUMMARY_ENDPOINT, {
                headers: {
                    accept: "application/json"
                }
            });

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            setFleetSummary(data);

        } catch (err) {

            console.error("Fleet summary check failed:", err);

            setFleetSummaryError(err.message || "Unable to reach fleet summary API");

            setFleetSummary(null);

        } finally {

            setFleetSummaryLoading(false);

        }

    }, []);

    const checkAll = useCallback(() => {

        setLastChecked(new Date());

        checkRestHealth();

        if (showSoapCard) {

            checkSoapHealth();

        }

        checkLastSync();

        checkFleetSummary();

        fetchLogs(fromDate, toDate, logLimit);

    }, [checkRestHealth, checkSoapHealth, checkLastSync, checkFleetSummary, fetchLogs, fromDate, toDate, logLimit]);

    // Always call the latest checkAll (it closes over fromDate/toDate),
    // without needing to restart the interval every time those change.
    const checkAllRef = useRef(checkAll);

    useEffect(() => {

        checkAllRef.current = checkAll;

    }, [checkAll]);

    useEffect(() => {

        checkAllRef.current();

        intervalRef.current = setInterval(() => checkAllRef.current(), AUTO_REFRESH_MS);

        return () => clearInterval(intervalRef.current);

    }, []); // set up once on mount; checkAllRef always has the latest closure

    const handleApplyFilters = () => {

        fetchLogs(fromDate, toDate, logLimit);

    };

    const handleResetFilters = () => {

        const today = getTodayDateString();

        setFromDate(today);

        setToDate(today);

        setStatusFilter("all");

        setLogLimit(100);

        fetchLogs(today, today, 100);

    };

    const filteredLogs = logs.filter((log) => {

        if (statusFilter === "success") {
            return log.status === true;
        }

        if (statusFilter === "failed") {
            return log.status === false;
        }

        return true;

    });

    const anyLoading = healthLoading || fleetSummaryLoading || (showSoapCard && soapLoading);

    const allOk =
        !healthLoading &&
        (!showSoapCard || !soapLoading) &&
        health?.all_ok &&
        lastSync?.status &&
        !fleetSummaryLoading &&
        !fleetSummaryError &&
        fleetSummary &&
        fleetSummary.offline === 0;

    return (

        <div className="dashboard">

            <div className="dashboard-header">

                <div>
                    <h2>Dashboard</h2>
                    <p className="dashboard-subtitle">
                        Welcome to Dashboard -- live service health overview
                    </p>
                </div>

                <div className="dashboard-actions">

                    {

                        lastChecked &&

                        <span className="last-checked">
                            <FaClock />
                            Last checked: {lastChecked.toLocaleTimeString()}
                        </span>

                    }

                    <button
                        type="button"
                        className="refresh-btn"
                        onClick={checkAll}
                        disabled={anyLoading}
                    >

                        <FaSyncAlt className={anyLoading ? "spin" : ""} />

                        {anyLoading ? "Checking..." : "Refresh"}

                    </button>

                </div>

            </div>

            <div className={`overall-banner ${anyLoading ? "banner-checking" : allOk ? "banner-ok" : "banner-down"}`}>

                {

                    anyLoading

                        ? <><FaSyncAlt className="spin" /> Checking all services...</>

                        : allOk

                            ? <><FaCheckCircle /> All systems operational</>

                            : <><FaTimesCircle /> One or more services are experiencing issues</>

                }

            </div>

            <div className="service-grid">

                <CombinedServiceCard
                    icon={<FaDatabase />}
                    title="LLM Service & Database"
                    loading={healthLoading}
                    errorMessage={healthError}
                    checkedAt={health?.checked_at}
                    subItems={[
                        {
                            label: "Database",
                            ok: health?.services?.database?.ok,
                            message: health?.services?.database?.message
                        },
                        {
                            label: "LLM Service",
                            ok: health?.services?.llm?.ok,
                            message: health?.services?.llm?.message
                        }
                    ]}
                />

                <FleetSummaryCard
                    loading={fleetSummaryLoading}
                    summary={fleetSummary}
                    errorMessage={fleetSummaryError}
                />
                {showSoapCard && (
                <ServiceCard                
                    icon={<FaServer />}
                    title="SOAP API"
                    loading={soapLoading}
                    ok={soap?.ok}                
                    message={
                        soapError
                            ? soapError
                            : soap?.message
                    }
                    extra={

                        soap &&

                        <>
                            <span className="extra-line">
                                Database: {soap.databaseStatus}
                            </span>

                            <span className="extra-line">
                                Checked at: {formatDateTime(soap.dateTime)}
                            </span>
                        </>

                    }
                /> 
                )}
                <ServiceCard
                    icon={<FaHistory />}
                    title="AODB Last Sync"
                    loading={lastSyncLoading}
                    ok={lastSync?.status}
                    message={
                        lastSyncError
                            ? lastSyncError
                            : lastSync?.remarks
                    }
                    extra={

                        lastSync &&

                        <>
                            <span className="extra-line">
                                API: {lastSync.api_name}
                            </span>

                            <span className="extra-line">
                                Synced at: {formatDateTime(lastSync.created_at)}
                            </span>
                        </>

                    }
                />

            </div>

            <div className="log-section">

                <div className="log-section-header">

                    <div>
                        <h3>Transaction Log</h3>
                        <p className="log-section-note">Showing today's data by default</p>
                    </div>

                    <div className="log-filters">

                        <select
                            className="filter-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">All Status</option>
                            <option value="success">Success</option>
                            <option value="failed">Failed</option>
                        </select>

                        <select
                            className="filter-select"
                            value={logLimit}
                            disabled={logsLoading}
                            onChange={(e) => {

                                const newLimit = Number(e.target.value);

                                setLogLimit(newLimit);

                                fetchLogs(fromDate, toDate, newLimit);

                            }}
                            title="Number of records to load"
                        >
                            <option value={100}>100 records</option>
                            <option value={200}>200 records</option>
                            <option value={300}>300 records</option>
                            <option value={500}>500 records</option>
                        </select>

                        <input
                            type="date"
                            className="filter-date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                        />

                        <span className="filter-separator">to</span>

                        <input
                            type="date"
                            className="filter-date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                        />

                        <button
                            type="button"
                            className="filter-btn filter-apply"
                            onClick={handleApplyFilters}
                            disabled={logsLoading}
                        >
                            Apply
                        </button>

                        <button
                            type="button"
                            className="filter-btn filter-reset"
                            onClick={handleResetFilters}
                            disabled={logsLoading}
                        >
                            Reset
                        </button>

                    </div>

                </div>

                <div className="log-table-wrapper">

                    <table className="log-table">

                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>API Name</th>
                                <th>Status</th>
                                <th>Remarks</th>
                                <th>Request Received</th>
                                <th>Insert Started</th>
                                <th>Insert Completed</th>
                            </tr>
                        </thead>

                        <tbody>

                            {

                                logsLoading &&

                                <tr>
                                    <td colSpan={7} className="log-table-message">
                                        <FaSyncAlt className="spin" /> Loading logs...
                                    </td>
                                </tr>

                            }

                            {

                                !logsLoading && logsError &&

                                <tr>
                                    <td colSpan={7} className="log-table-message log-table-error">
                                        {logsError}
                                    </td>
                                </tr>

                            }

                            {

                                !logsLoading && !logsError && filteredLogs.length === 0 &&

                                <tr>
                                    <td colSpan={7} className="log-table-message">
                                        No log records found.
                                    </td>
                                </tr>

                            }

                            {

                                !logsLoading && !logsError &&

                                filteredLogs.map((log) => (

                                    <tr key={log.id}>

                                        <td>{log.id}</td>

                                        <td>{log.api_name}</td>

                                        <td>
                                            <StatusBadge ok={log.status} />
                                        </td>

                                        <td>{log.remarks || "-"}</td>

                                        <td>{formatDateTime(log.request_received_at)}</td>

                                        <td>{formatDateTime(log.db_insert_started_at)}</td>

                                        <td>{formatDateTime(log.db_insert_completed_at)}</td>

                                    </tr>

                                ))

                            }

                        </tbody>

                    </table>

                </div>

            </div>

        </div>

    );

}

export default Dashboard;